"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  CHROME,
  DARK_QUERY,
  THEME_STORAGE_KEY,
  parsePreference,
  resolveTheme,
  type Theme,
  type ThemePreference,
} from "@/lib/ui/theme-preference";

// Re-exported so components can type a menu handler without reaching past the
// hook they already import.
export type { ThemePreference };

interface ThemeContextType {
  /** The resolved theme. This is what components should render against. */
  theme: Theme;
  /** The stored choice, which is what the menu should tick. */
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  /** Flip to the opposite of what is showing, as an explicit choice. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

/** What the operating system is set to right now. */
function systemTheme(): Theme {
  try {
    return window.matchMedia?.(DARK_QUERY).matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** The stored choice, or "system" when there is nothing usable saved. */
function readPreference(): ThemePreference {
  try {
    return parsePreference(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
}

function resolve(preference: ThemePreference): Theme {
  return resolveTheme(preference, systemTheme() === "dark");
}

/** Everything outside React that has to move when the theme does. */
function applyTheme(theme: Theme) {
  try {
    document.documentElement.classList.toggle("dark", theme === "dark");

    // EVERY theme-color tag, not the first one. The layout ships two, scoped
    // to (prefers-color-scheme: light) and (prefers-color-scheme: dark),
    // because Android's installed app wants the right colour in the HTML
    // rather than written in afterwards. Setting only the first would leave
    // the other one contradicting it the moment someone PINS a theme their
    // phone disagrees with. Both say the same thing, so it no longer matters
    // which the browser picks.
    let metas = Array.from(document.querySelectorAll('meta[name="theme-color"]'));
    if (metas.length === 0) {
      // Next emits these from the viewport export, but create one rather than
      // silently do nothing if that ever stops being true.
      const meta = document.createElement("meta");
      meta.setAttribute("name", "theme-color");
      document.head.appendChild(meta);
      metas = [meta];
    }
    for (const meta of metas) meta.setAttribute("content", CHROME[theme]);
  } catch {}
}

// Always start with light on both server and client to avoid a hydration
// mismatch — the server cannot know either the stored choice or the Mac's
// setting. The real theme is applied in useLayoutEffect, before paint.

export function ThemeProvider({ children }: { children: ReactNode }) {
  // One piece of state, not two: preference and theme always change together,
  // and splitting them would mean two setState calls inside the layout effect
  // below where one will do.
  const [{ theme, preference }, setState] = useState<{
    theme: Theme;
    preference: ThemePreference;
  }>({ theme: "light", preference: "system" });

  useLayoutEffect(() => {
    const saved = readPreference();
    const resolved = resolve(saved);
    setState({ theme: resolved, preference: saved });
    applyTheme(resolved);
  }, []);

  // Follow the OS, but only while the choice is "system". Someone who pinned
  // light does not want their screen changing at sunset.
  useEffect(() => {
    if (preference !== "system") return;
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(DARK_QUERY);
    } catch {
      return;
    }
    const onChange = () => {
      const next = systemTheme();
      setState({ theme: next, preference: "system" });
      applyTheme(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {}
    const resolved = resolve(next);
    setState({ theme: resolved, preference: next });
    applyTheme(resolved);
  }, []);

  const toggleTheme = useCallback(() => {
    // A deliberate flip is a deliberate choice, so it pins. Otherwise turning
    // on dark while the Mac is light would appear to do nothing the next time
    // the Mac changed its mind.
    setPreference(theme === "light" ? "dark" : "light");
  }, [theme, setPreference]);

  return (
    <ThemeContext.Provider value={{ theme, preference, setPreference, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
